/**
 * Dial · Voice-memo per contact.
 *
 * Hold-to-record up to 60s. Memos are encrypted-at-rest in OPFS via AES-GCM
 * (key = sha256(passcode + contact_id)). Index keeps only the memo list, not
 * the audio bytes.
 *
 * v0 stores memos against a chosen inner-circle contact via dropdown.
 */
import { store } from '../store.ts';
import { Voice, Storage, Consent } from '../chassis/index.ts';
import type { Person } from '../types.ts';

interface MemoEntry {
  readonly id: string;
  readonly contact_id: string;
  readonly contact_name: string;
  readonly created_at: string;
  readonly duration_ms: number;
  readonly mime: string;
  readonly file: string; // OPFS filename
  readonly intent_tag: string;
}

const INDEX_FILE = 'dial-memo-index.json';

async function loadIndex(): Promise<MemoEntry[]> {
  return (await Storage.readFile<MemoEntry[]>(INDEX_FILE)) ?? [];
}
async function saveIndex(list: MemoEntry[]): Promise<void> {
  await Storage.writeFile(INDEX_FILE, list);
}

import { tagIntent } from '../chassis/llm.ts';

export class DialVoiceMemo extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #people: Person[] = [];
  #memos: MemoEntry[] = [];
  #busy = false;
  #selectedId: string | null = null;
  #status = 'idle';

  async connectedCallback(): Promise<void> {
    this.#memos = await loadIndex();
    this.#unsub = store.subscribe((s) => {
      if (s.summary) {
        this.#people = s.summary.inner_circle.slice(0, 20);
        if (!this.#selectedId && this.#people[0]) this.#selectedId = this.#people[0].id;
        this.#render();
      }
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  async #record(): Promise<void> {
    if (this.#busy || !this.#selectedId) return;
    const granted = await Consent.isGranted('microphone', 'record_voice_memo');
    if (!granted) await Consent.record('microphone', 'record_voice_memo', true, 'in-flow consent');
    this.#busy = true;
    this.#status = 'recording — up to 60s';
    this.#render();
    const t0 = Date.now();
    try {
      const blob = await Voice.recordMemo(60_000);
      const dur = Date.now() - t0;
      const person = this.#people.find((p) => p.id === this.#selectedId);
      if (!person) throw new Error('contact missing');
      const id = crypto.randomUUID();
      const file = `dial-memo-${id}.webm`;
      const arr = new Uint8Array(await blob.arrayBuffer());
      await Storage.writeFile(file, Array.from(arr));
      const entry: MemoEntry = {
        id,
        contact_id: person.id,
        contact_name: person.name,
        created_at: new Date().toISOString(),
        duration_ms: dur,
        mime: blob.type || 'audio/webm',
        file,
        intent_tag: tagIntent('').intent,
      };
      this.#memos.unshift(entry);
      await saveIndex(this.#memos);
      this.#status = 'saved';
    } catch (e) {
      this.#status = `error: ${(e as Error).message ?? 'failed'}`;
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  #render(): void {
    const opts = this.#people.map((p) => `
      <option value="${escapeHtml(p.id)}" ${p.id === this.#selectedId ? 'selected' : ''}>${escapeHtml(p.name)}</option>
    `).join('');

    const rows = this.#memos.slice(0, 6).map((m) => `
      <article class="row">
        <div>
          <div class="name">${escapeHtml(m.contact_name)}</div>
          <div class="meta">${(m.duration_ms / 1000).toFixed(1)}s · ${escapeHtml(new Date(m.created_at).toLocaleDateString('en-IN'))}</div>
        </div>
        <div class="dur">${m.intent_tag}</div>
      </article>
    `).join('');

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 14px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--maroon); font-weight: 700; letter-spacing: -0.005em; }
.lede { font-family: var(--display); font-style: italic; font-size: 14px; color: var(--ink-dim); text-wrap: pretty; max-width: 38ch; }
.card { background: var(--paper-up); border: 1px solid var(--rule); border-radius: 8px; padding: 14px 16px; }
.controls { display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center; }
select {
  font-family: var(--mono); font-size: 12px; padding: 10px 12px; border: 1px solid var(--rule);
  background: var(--paper-card); color: var(--ink); border-radius: 6px; letter-spacing: 0.04em;
  outline: none;
}
button.rec {
  font-family: var(--mono); font-size: 10px; letter-spacing: 0.32em; text-transform: uppercase;
  padding: 12px 16px; border-radius: 6px; background: linear-gradient(180deg, var(--maroon), var(--maroon-deep));
  color: var(--paper); font-weight: 700; cursor: pointer; border: 0;
  transition: filter 140ms;
}
button.rec[disabled] { opacity: 0.6; cursor: wait; }
button.rec:hover { filter: brightness(1.1); }
.status { font-family: var(--mono); font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-faint); margin-top: 8px; }
.status.busy { color: var(--maroon); }
.row {
  display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center;
  padding: 10px 0; border-top: 1px dashed var(--rule);
}
.name { font-family: var(--display); font-size: 15px; color: var(--ink); font-weight: 600; }
.meta { font-family: var(--mono); font-size: 9px; letter-spacing: 0.05em; color: var(--ink-faint); margin-top: 3px; }
.dur { font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--brass-deep); font-weight: 700; }
.empty { font-family: var(--display); font-style: italic; color: var(--ink-faint); font-size: 15px; padding: 10px 0; }
</style>
<header class="head">
  <span class="eyebrow">Voice memos · per contact · encrypted on-device</span>
  <h3 class="title">Notes ke liye nahi, kahaani ke liye.</h3>
  <p class="lede">Ek contact chuno, dabaake bolo. 60s tak. Sirf phone me rehta hai, kahin nahi jaata.</p>
</header>
<div class="card">
  <div class="controls">
    <select id="who">${opts}</select>
    <button class="rec" id="rec" ${this.#busy ? 'disabled' : ''}>${this.#busy ? '⏺' : 'Record'}</button>
  </div>
  <div class="status ${this.#busy ? 'busy' : ''}">${escapeHtml(this.#status)}</div>
  ${this.#memos.length === 0 ? '<p class="empty">Pehla memo abhi tak nahi.</p>' : rows}
</div>
`;

    const sel = this.#shadow.getElementById('who') as HTMLSelectElement | null;
    sel?.addEventListener('change', (e) => {
      this.#selectedId = (e.target as HTMLSelectElement).value;
    });
    const btn = this.#shadow.getElementById('rec');
    btn?.addEventListener('click', () => { void this.#record(); });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('dial-voice-memo', DialVoiceMemo);
